import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Paper, Card, CardContent } from "@mui/material";
import { useHistory } from "react-router-dom";
import useStyles from "../../assets/styles/HomeStyles";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const DQProSalesAnalytics = () => {
    const classes = useStyles();
    const history = useHistory();
    const [tabValue, setTabValue] = useState(0);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const metrics = {
        licensesUsed: 5000,
        licensesAssigned: 6000,
        referralEarnings: 3000,
        referralCode: 'XCDVGD33'
    };

    // Data for horizontal bar chart (Licenses by Client)
    const clientData = [
        { name: 'Client A', used: 12, assigned: 15 },
        { name: 'Client B', used: 8, assigned: 10 },
        { name: 'Client C', used: 20, assigned: 18 },
        { name: 'Client D', used: 15, assigned: 20 },
        { name: 'Client E', used: 5, assigned: 8 },
    ];

    // Data for vertical bar chart (Daily Licenses)
    const dailyData = [
        { day: '1', licenses: 2 },
        { day: '2', licenses: 4 },
        { day: '3', licenses: 1 },
        { day: '4', licenses: 6 },
        { day: '5', licenses: 3 },
        { day: '6', licenses: 5 },
        { day: '7', licenses: 7 },
        { day: '8', licenses: 4 },
        { day: '9', licenses: 6 },
        { day: '10', licenses: 8 },
        { day: '11', licenses: 5 },
        { day: '12', licenses: 7 },
        { day: '13', licenses: 9 },
        { day: '14', licenses: 10 },
    ];

    return (
        <Box className={classes.pageContainer} sx={{ margin: { xs: '0', sm: 'auto' } }}>
            <Typography variant="h2" className={classes.title} sx={{ mb: 2 }}>
                Sales Analytics
            </Typography>

            {/* Tabs */}
            <Box sx={{ mb: 4, minWidth:'680px' }}>
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    TabIndicatorProps={{ style: { display: 'none' } }}
                >
                    <Tab label="This Month" style={{
                        borderRadius:'13px',
                        margin:'20px',
                        color: tabValue === 0 ? '#ffffff' : '#000000',
                        backgroundColor: tabValue === 0 ? '#ff6468ff' : '#d9d9d9',
                        '&:hover': { backgroundColor: tabValue === 0 ? '#ff6468ff' : '#d9d9d9', borderColor:'#ff6468ff' }
                    }} />
                    <Tab label="This Year" style={{
                        borderRadius:'13px',
                        margin:'20px',
                        color: tabValue === 1 ? '#ffffff' : '#000000',
                        backgroundColor: tabValue === 1 ? '#ff6468ff' : '#d9d9d9',
                        '&:hover': { backgroundColor: tabValue === 1 ? '#ff6468ff' : '#d9d9d9', borderColor:'#ff6468ff' }
                    }} />
                    <Tab label="Select Time Range" style={{
                        borderRadius:'13px',
                        margin:'20px',
                        color: tabValue === 2 ? '#ffffff' : '#000000',
                        backgroundColor: tabValue === 2 ? '#ff6468ff' : '#d9d9d9',
                        '&:hover': { backgroundColor: tabValue === 2 ? '#ff6468ff' : '#d9d9d9', borderColor:'#ff6468ff' }
                    }} />
                </Tabs>
            </Box>
            
        <Box sx={{ width:'100%',minWidth:'1200px',margin: { xs: '0', sm: 'auto' } }}>
            {/* Metrics Cards */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                mb: 4,
                width:'80%',
                mx:'auto',
            }}>
                {/* Card 1 */}
                <Card sx={{
                    flex: 1,
                    border: '2px solid #ff6468ff',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    textAlign:'center',
                    display:'flex',
                    flexDirection:'column',
                    alignItems:'center',
                    justifyContent:'center',
                    py:2,
                }}>
                    <CardContent>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                            {metrics.licensesUsed.toLocaleString()}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Licenses Used By
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Your Clients
                        </Typography>
                    </CardContent>
                </Card>

                {/* Card 2 */}
                <Card sx={{
                    flex: 1,
                    border: '2px solid #ff6468ff',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    textAlign:'center',
                    display:'flex',
                    flexDirection:'column',
                    alignItems:'center',
                    justifyContent:'center',
                    py:2,
                }}>
                    <CardContent>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                            {metrics.licensesAssigned.toLocaleString()}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Assigned Licenses to
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Your Clients
                        </Typography>
                    </CardContent>
                </Card>

                {/* Card 3 */}
                <Card sx={{
                    flex: 1,
                    border: '2px solid #ff6468ff',
                    borderRadius: '8px',
                    boxShadow: 'none',
                    textAlign:'center',
                    display:'flex',
                    flexDirection:'column',
                    alignItems:'center',
                    justifyContent:'center',
                    py:2,
                }}>
                    <CardContent>
                        <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                            ${metrics.referralEarnings.toLocaleString()}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Referral Earnings
                        </Typography>
                        <Typography variant="caption" color="#ff6468ff">
                            Your Referral Code: {metrics.referralCode}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Charts */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 3,
                mb: 4,
                height: '400px'
            }}>
                {/* Horizontal Bar Chart */}
                <Box sx={{ flex: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            layout="vertical"
                            data={clientData}
                            margin={{ top: 20, right: 30, left: 40, bottom: 5 }}
                        >
                            <XAxis type="number" domain={[0, 50]} />
                            <YAxis dataKey="name" type="category" />
                            <Tooltip
                                contentStyle={{
                                    borderRadius: '8px',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                }}
                                formatter={(value, name) => [value, name]}
                                labelFormatter={(label) => label}
                                cursor={{ fill: 'transparent' }}
                            />
                            <Legend
                                align="right"
                                verticalAlign="top"
                                layout="horizontal"
                                wrapperStyle={{
                                    paddingLeft: '30px',
                                    paddingBottom: '10px'
                                }}
                            />
                            <Bar
                                dataKey="used"
                                fill="#6ce5e8"
                                name="Licenses Used"
                                radius={[0, 4, 4, 0]}
                            />
                            <Bar
                                dataKey="assigned"
                                fill="#0097b2"
                                name="Licenses Assigned"
                                radius={[0, 4, 4, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>

                {/* Vertical Bar Chart */}
                <Box sx={{ flex: 1 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={dailyData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                            <XAxis dataKey="day" />
                            <YAxis domain={[0, 14]} />
                            <Tooltip
                                contentStyle={{
                                    borderRadius: '8px',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                }}
                                formatter={(value) => [value]}
                                labelFormatter={(label) => `Day ${label}`}
                                cursor={{ fill: 'transparent' }}
                            />
                            <Legend
                                align="right"
                                verticalAlign="top"
                                wrapperStyle={{
                                    paddingLeft: '30px',
                                    paddingBottom: '10px'
                                }}
                            />
                            <Bar
                                dataKey="licenses"
                                fill="#6ce5e8"
                                name="Licenses Used"
                                radius={[4, 4, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </Box>
            </Box>
        </Box>
      </Box>
    );
};

export default DQProSalesAnalytics;